// Cloudinary configuration for image handling
// Environment variables should be set in your .env file

export const cloudinaryConfig = {
  cloudName: import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || 'demo',
  uploadPreset: import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET || 'unsigned_preset',
  apiKey: import.meta.env.VITE_CLOUDINARY_API_KEY || '',
  apiSecret: import.meta.env.VITE_CLOUDINARY_API_SECRET || ''
}

/**
 * Upload an image to Cloudinary
 * @param file - The file to upload
 * @param folder - Optional folder name for organization
 * @returns Promise with the upload result containing secure_url
 */
export async function uploadImage(
  file: File,
  folder: string = 'garage-mkononi'
): Promise<{ secure_url: string; public_id: string }> {
  console.log('Upload attempt:', file.name, file.size, 'bytes')
  
  // Fallback to local file conversion if Cloudinary is not configured
  if (!cloudinaryConfig.cloudName || cloudinaryConfig.cloudName === 'demo') {
    console.log('Cloudinary not configured, using local file conversion')
    return convertToBase64(file)
  }

  const formData = new FormData()
  formData.append('file', file)
  formData.append('upload_preset', cloudinaryConfig.uploadPreset)
  formData.append('folder', folder)

  try {
    console.log('Attempting Cloudinary upload to:', cloudinaryConfig.cloudName)
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudinaryConfig.cloudName}/image/upload`,
      {
        method: 'POST',
        body: formData
      }
    )

    if (!response.ok) {
      const errorText = await response.text()
      console.warn('Cloudinary upload failed:', response.status, errorText)
      console.log('Falling back to local file conversion')
      return convertToBase64(file)
    }

    const data = await response.json()
    console.log('Cloudinary upload successful:', data.secure_url)
    return {
      secure_url: data.secure_url,
      public_id: data.public_id
    }
  } catch (error) {
    console.error('Error uploading image to Cloudinary:', error)
    console.log('Falling back to local file conversion')
    return convertToBase64(file)
  }
}

/**
 * Convert file to base64 as fallback when Cloudinary is not available
 * @param file - The file to convert
 * @returns Promise with base64 URL
 */
async function convertToBase64(file: File): Promise<{ secure_url: string; public_id: string }> {
  return new Promise((resolve, reject) => {
    console.log('Converting file to base64:', file.name)
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const base64 = reader.result as string
      console.log('Base64 conversion successful, length:', base64.length)
      resolve({
        secure_url: base64,
        public_id: `local_${Date.now()}_${file.name}`
      })
    }
    reader.onerror = error => {
      console.error('Base64 conversion error:', error)
      reject(error)
    }
  })
}

/**
 * Delete an image from Cloudinary
 * @param publicId - The public ID of the image to delete
 * @returns Promise indicating success/failure
 */
export async function deleteImage(publicId: string): Promise<boolean> {
  try {
    const formData = new FormData()
    formData.append('public_id', publicId)
    formData.append('api_key', cloudinaryConfig.apiKey)
    formData.append('signature', generateSignature(publicId))
    formData.append('timestamp', Math.floor(Date.now() / 1000).toString())

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudinaryConfig.cloudName}/image/destroy`,
      {
        method: 'POST',
        body: formData
      }
    )

    if (!response.ok) {
      throw new Error('Failed to delete image')
    }

    const data = await response.json()
    return data.result === 'ok'
  } catch (error) {
    console.error('Error deleting image from Cloudinary:', error)
    return false
  }
}

/**
 * Generate signature for authenticated Cloudinary operations
 * Note: This should be done server-side in production for security
 */
function generateSignature(publicId: string): string {
  // In production, this should be done on your backend server
  // For now, we'll use unsigned uploads which don't require signatures
  return ''
}

/**
 * Get optimized image URL
 * @param url - The original Cloudinary URL
 * @param options - Transformation options
 * @returns Optimized image URL
 */
export function getOptimizedImageUrl(
  url: string,
  options: {
    width?: number
    height?: number
    quality?: number
    format?: 'webp' | 'jpg' | 'png' | 'auto'
  } = {}
): string {
  if (!url.includes('cloudinary.com')) {
    return url
  }

  const { width, height, quality = 80, format = 'auto' } = options

  // Build transformation string
  const transformations = []
  if (width) transformations.push(`w_${width}`)
  if (height) transformations.push(`h_${height}`)
  transformations.push(`q_${quality}`)
  transformations.push(`f_${format}`)

  const transformationString = transformations.join(',')

  // Insert transformation into URL
  const urlParts = url.split('/upload/')
  if (urlParts.length === 2) {
    return `${urlParts[0]}/upload/${transformationString}/${urlParts[1]}`
  }

  return url
}

/**
 * Get thumbnail URL
 * @param url - The original Cloudinary URL
 * @param size - Thumbnail size (default: 200)
 * @returns Thumbnail image URL
 */
export function getThumbnailUrl(url: string, size: number = 200): string {
  return getOptimizedImageUrl(url, {
    width: size,
    height: size,
    quality: 70,
    format: 'webp'
  })
}